// Health.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from 'recharts';
import { Head } from 'components/Head';
import { Spinner, Button } from 'components/Elements';
import {
  API_URL,
  BUSINESS_API_URL,
  NOTIFICATIONS_API_URL,
  PAYMENTS_API_URL,
  RIDE_HAILING_API_URL,
  MESSAGING_API_URL,
} from 'config';
import { InputField } from 'components/Form';

/**
 * Health dashboard with:
 * - sparklines (uptime history)
 * - 24h availability chart
 * - monthly uptime percentage calculator
 * - response-time chart per service
 * - webhook registration + push notifications when service goes down
 * - "All systems operational" banner
 * - dark mode toggle persisted in localStorage
 *
 * Notes:
 * - If your services provide history endpoints (e.g. /health/history or /health/uptime),
 *   adapt FETCH_HISTORY to point to them. Otherwise the component synthesizes short-term history
 *   from repeated /health checks (basic but useful).
 */

/* -------------------------
   Configuration
   ------------------------- */
const SERVICES = [
  { key: 'main', name: 'Main API', base: API_URL },
  { key: 'business', name: 'Business', base: BUSINESS_API_URL },
  { key: 'notifications', name: 'Notifications', base: NOTIFICATIONS_API_URL },
  { key: 'payments', name: 'Payments', base: PAYMENTS_API_URL },
  { key: 'ride', name: 'Ride Hailing', base: RIDE_HAILING_API_URL },
  { key: 'messaging', name: 'Messaging', base: MESSAGING_API_URL },
];

const HEALTH_PATH = '/health';
const HISTORY_PATHS = [
  '/health/history?period=24h',
  '/health/uptime?range=24h',
  '/health/metrics?range=24h',
]; // try these (in order) if your service exposes history; otherwise synthesize

/* -------------------------
   Utilities
   ------------------------- */
const STATUS_PRIORITY = { online: 3, degraded: 2, maintenance: 1, offline: 0 };

const normalizeStatus = (res) => {
  // Accept common shapes, fall back to 'offline' when unreachable
  if (!res || !res.name) return 'offline';
  if (!res.healthy) return 'offline';
  const s = (res.status || '').toString().toLowerCase();
  if (s.includes('degrad')) return 'degraded';
  if (s.includes('maint')) return 'maintenance';
  if (s.includes('down') || s.includes('fail') || s.includes('error')) return 'offline';
  return 'online';
};

const statusLabel = {
  online: 'Operational',
  degraded: 'Degraded',
  offline: 'Major Outage',
  maintenance: 'Maintenance',
};

const statusColor = {
  online: 'text-green-600 bg-green-50',
  degraded: 'text-yellow-600 bg-yellow-50',
  offline: 'text-red-600 bg-red-50',
  maintenance: 'text-gray-700 bg-gray-100',
};

const nowISO = () => new Date().toISOString();

/* -------------------------
   Fetchers
   ------------------------- */
async function fetchHealthForService(service) {
  const url = `${service.base.replace(/\/+$/, '')}${HEALTH_PATH}`;
  const started = Date.now();
  try {
    const res = await fetch(url, { cache: 'no-store' });
    const json = await res.json().catch(() => ({}));
    return {
      name: service.name,
      key: service.key,
      healthy: res.ok,
      status: json?.status ?? json?.state ?? (res.ok ? 'ok' : 'down'),
      message: json?.message ?? '',
      responseTime: Date.now() - started,
      checkedAt: nowISO(),
    };
  } catch (err) {
    return {
      name: service.name,
      key: service.key,
      healthy: false,
      status: 'down',
      message: 'Service unreachable',
      responseTime: null,
      checkedAt: nowISO(),
    };
  }
}

async function tryFetchHistory(service) {
  // Try a small list of possible history endpoints the service might expose.
  for (const p of HISTORY_PATHS) {
    const url = `${service.base.replace(/\/+$/, '')}${p}`;
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) continue;
      const json = await res.json();
      // Expecting an array of points: { timestamp, status, responseTime } or similar.
      if (Array.isArray(json) && json.length) {
        return json.map((pt) => ({
          timestamp: pt.timestamp || pt.time || pt.t || nowISO(),
          status: pt.status || pt.state || 'online',
          responseTime: pt.responseTime ?? pt.latency ?? null,
        }));
      }
    } catch (e) {
      // ignore and try next
    }
  }
  // not available
  return null;
}

/* -------------------------
   Main Component
   ------------------------- */
export function Health() {
  const queryClient = useQueryClient();
  const [dark, setDark] = useState(() => {
    try {
      const v = localStorage.getItem('jk:health:dark');
      if (v === null) return true; // default to dark
      return v === '1';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('jk:health:dark', dark ? '1' : '0');
      if (dark) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
    } catch {}
  }, [dark]);

  // store previous statuses to detect changes (for notifications/webhooks)
  const prevStatusRef = useRef({});
  // user-saved webhooks: { [serviceKey]: webhookUrl }
  const [webhooks, setWebhooks] = useState(() => {
    try {
      const raw = localStorage.getItem('jk:health:webhooks') || '{}';
      return JSON.parse(raw);
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('jk:health:webhooks', JSON.stringify(webhooks));
    } catch {}
  }, [webhooks]);

  // fetch latest health for each service
  const {
    data: healthData,
    isLoading,
    isFetching,
  } = useQuery(
    ['jk:health:all'],
    async () => {
      const results = await Promise.all(SERVICES.map((s) => fetchHealthForService(s)));
      return results.map((r) => ({
        ...r,
        normalized: normalizeStatus(r),
      }));
    },
    {
      refetchInterval: 10000,
      refetchIntervalInBackground: true,
      staleTime: 5000,
    }
  );

  // fetch per-service history (24h) when available; otherwise synthesize from repeated health checks saved in localStorage
  const [histories, setHistories] = useState({}); // { key: [{timestamp, status, responseTime}] }

  useEffect(() => {
    let mounted = true;
    async function loadHistories() {
      const next = {};
      await Promise.all(
        SERVICES.map(async (s) => {
          const h = await tryFetchHistory(s);
          if (h) {
            next[s.key] = h.map((pt) => ({
              timestamp: pt.timestamp,
              status: normalizeStatus({ name: s.name, healthy: true, status: pt.status }),
              responseTime: pt.responseTime ?? null,
            }));
          } else {
            // fallback: try to read from localStorage synthetic history
            try {
              const raw = localStorage.getItem(`jk:health:history:${s.key}`);
              if (raw) {
                const parsed = JSON.parse(raw);
                next[s.key] = parsed;
              } else {
                next[s.key] = []; // empty for now; will build from live checks below
              }
            } catch {
              next[s.key] = [];
            }
          }
        })
      );
      if (mounted) setHistories((prev) => ({ ...prev, ...next }));
    }
    loadHistories();
    return () => {
      mounted = false;
    };
  }, []); // run once

  // Append latest check to synthetic history (and persist small window)
  useEffect(() => {
    if (!healthData) return;
    const nextHist = { ...histories };
    let changed = false;
    for (const item of healthData) {
      const key = item.key;
      if (!nextHist[key]) nextHist[key] = [];
      // push a new point
      nextHist[key] = [
        ...nextHist[key],
        {
          timestamp: item.checkedAt,
          status: item.normalized,
          responseTime: item.responseTime,
        },
      ]
        // keep last 288 points (~every 5 min for 24h if you want); here keep 288 points as upper bound
        .slice(-288);
      changed = true;
    }
    if (changed) {
      setHistories(nextHist);
      // persist synthetic history to localStorage as fallback
      try {
        for (const k of Object.keys(nextHist)) {
          localStorage.setItem(`jk:health:history:${k}`, JSON.stringify(nextHist[k]));
        }
      } catch {}
    }
  }, [healthData]);

  // detect status changes -> push notification + webhook call
  useEffect(() => {
    if (!healthData) return;
    for (const s of healthData) {
      const prev = prevStatusRef.current[s.key];
      const curr = s.normalized;
      if (prev && prev !== curr) {
        // status changed
        // 1) Browser push notification
        try {
          if (Notification && Notification.permission === 'granted') {
            const title = `${s.name} — ${statusLabel[curr]}`;
            const body = `${s.name} changed status: ${statusLabel[prev]} → ${statusLabel[curr]}`;
            new Notification(title, { body, tag: `jk-health-${s.key}` });
          }
        } catch {}
        // 2) Trigger webhook if configured
        const webhookUrl = webhooks[s.key];
        if (webhookUrl) {
          // POST event payload; ignore result
          try {
            fetch(webhookUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                service: s.name,
                key: s.key,
                oldStatus: prev,
                newStatus: curr,
                time: s.checkedAt,
                message: s.message,
              }),
            }).catch(() => {});
          } catch {}
        }
      }
      prevStatusRef.current[s.key] = curr;
    }
  }, [healthData, webhooks]);

  // helper: request notification permission
  const requestNotificationPermission = async () => {
    try {
      if (!('Notification' in window)) {
        alert('This browser does not support desktop notifications.');
        return;
      }
      const p = await Notification.requestPermission();
      if (p === 'granted')
        alert('Notifications enabled — you will receive alerts while this tab is open.');
      else if (p === 'denied')
        alert('Notifications blocked. Change your browser settings to allow them.');
    } catch (e) {
      // ignore
    }
  };

  // Aggregations
  const aggregateAllOperational = useMemo(() => {
    if (!healthData) return false;
    return healthData.every((s) => s.normalized === 'online');
  }, [healthData]);

  // 24-hour availability chart: we sample each service's history and compute percent online per hour
  // We'll compute a single aggregated series (average availability across services) for the past 24 hours
  const availability24h = useMemo(() => {
    // Build 24 buckets (hours)
    const buckets = Array.from({ length: 24 }).map((_, i) => ({
      hourIndex: i,
      label: `${i}h`,
      availableCount: 0,
      totalCount: 0,
    }));

    if (!histories || Object.keys(histories).length === 0) {
      return buckets.map((b) => ({ ...b, uptimePct: 100 }));
    }

    const now = Date.now();
    for (const sKey of Object.keys(histories)) {
      const points = histories[sKey] || [];
      // for each point, place into bucket by hour difference
      for (const pt of points) {
        const t = new Date(pt.timestamp).getTime();
        const diffHours = Math.floor((now - t) / (1000 * 60 * 60));
        if (diffHours >= 0 && diffHours < 24) {
          const bucketIndex = 23 - diffHours; // recent is rightmost
          buckets[bucketIndex].totalCount += 1;
          if (pt.status === 'online') buckets[bucketIndex].availableCount += 1;
        }
      }
    }

    // compute uptime percent per bucket
    return buckets.map((b) => {
      const uptimePct = b.totalCount ? (b.availableCount / b.totalCount) * 100 : 100;
      return { ...b, uptimePct: Math.round(uptimePct * 100) / 100 };
    });
  }, [histories]);

  // Monthly uptime percentage calculator: approximate from history array (last 30 days)
  // If no 30-day history present, derive from available history points (best-effort)
  const monthlyUptime = useMemo(() => {
    const scores = {};
    for (const s of SERVICES) {
      const pts = (histories[s.key] || []).slice(-720); // up to last N points (best-effort)
      if (!pts.length) {
        scores[s.key] = 100;
        continue;
      }
      const up = pts.filter((p) => p.status === 'online').length;
      scores[s.key] = Math.round((up / pts.length) * 10000) / 100; // 2 decimal
    }
    return scores;
  }, [histories]);

  // response time series for selected service
  const [selectedService, setSelectedService] = useState(SERVICES[0].key);

  // webhook URL input local state
  const [webhookInputs, setWebhookInputs] = useState(webhooks);

  // handlers
  const handleSaveWebhook = (key) => {
    const url = (webhookInputs[key] || '').trim();
    const next = { ...webhooks };
    if (url) next[key] = url;
    else delete next[key];
    setWebhooks(next);
    alert(`Webhook ${url ? 'saved' : 'removed'} for ${key}`);
  };

  // UI rendering
  return (
    <>
      <Head title="Health" description="Live status and uptime dashboard for JustKnow services" />

      <div className={`min-h-screen py-8 px-4 bg-gray-50 text-gray-900`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold">JustKnow</h1>
              <p className="text-sm mt-1 text-gray-400">
                Live health checks, uptime history, response times and alerts.
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <div className="text-sm text-gray-400">
                <div>
                  Last refresh:{' '}
                  <span className="font-mono text-xs">{new Date().toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* All-systems banner */}
          <div className="mb-6">
            {aggregateAllOperational ? (
              <div className="rounded-lg p-4 border bg-green-600 text-white shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">All systems operational</div>
                    <div className="text-xs opacity-90">No incidents detected across services.</div>
                  </div>
                  <div className="text-sm">✅</div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg p-4 border bg-yellow-50 text-gray-800 shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">Partial degradation detected</div>
                    <div className="text-xs opacity-90">
                      Some services are degraded or offline — see cards below.
                    </div>
                  </div>
                  <div className="text-sm">⚠️</div>
                </div>
              </div>
            )}
          </div>

          {/* Grid: Cards + Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Service cards with sparklines & webhook inputs */}
            <div className="col-span-1 space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Spinner size="lg" />
                </div>
              ) : (
                healthData?.map((s) => {
                  const colorClass =
                    {
                      online: 'border-green-300',
                      degraded: 'border-yellow-300',
                      offline: 'border-red-300',
                      maintenance: 'border-gray-300',
                    }[s.normalized] || 'border-gray-200';

                  const sparkData = (histories[s.key] || []).slice(-24).map((pt) => ({
                    x: pt.timestamp,
                    y: pt.status === 'online' ? 1 : 0,
                  }));

                  // monthly percentage for this service
                  const monthly = monthlyUptime[s.key] ?? 100;

                  return (
                    <div
                      key={s.key}
                      className={`bg-white dark:bg-gray-800 rounded-2xl p-4 border ${colorClass} shadow-sm`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-semibold text-sm">{s.name}</div>
                          <div className="text-xs mt-1">{s.message || '—'}</div>
                          <div
                            className={`inline-flex items-center mt-2 px-2 py-1 rounded-full text-xs ${
                              statusColor[s.normalized]
                            }`}
                          >
                            {statusLabel[s.normalized]}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-xs text-gray-400">RT</div>
                          <div className="font-mono text-sm">
                            {s.responseTime ? `${s.responseTime} ms` : 'N/A'}
                          </div>
                        </div>
                      </div>

                      {/* Sparkline (uptime over last N points) */}
                      <div className="h-12 mt-3">
                        {sparkData.length > 1 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={sparkData}>
                              <defs>
                                <linearGradient id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#34d399" stopOpacity={0.5} />
                                  <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <Area
                                type="monotone"
                                dataKey="y"
                                stroke="#10b981"
                                fill={`url(#grad-${s.key})`}
                                strokeWidth={2}
                              />
                              <XAxis dataKey="x" hide />
                              <YAxis hide domain={[0, 1]} />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-xs text-gray-400">No recent history</div>
                        )}
                      </div>

                      {/* Monthly uptime */}
                      <div className="mt-3 text-xs text-gray-500">
                        <div>
                          Monthly uptime: <span className="font-medium">{monthly}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Middle: 24h availability aggregated chart */}
            <div className="col-span-1 lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">24-hour availability (aggregated)</h3>
                  <p className="text-xs text-gray-400">
                    Average percent of services online per hour
                  </p>
                </div>
                <div className="text-xs text-gray-400">
                  Auto-refresh every 10s {isFetching ? '• fetching…' : ''}
                </div>
              </div>

              <div className="h-40 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={availability24h}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip formatter={(val) => `${val}%`} />
                    <Bar dataKey="uptimePct" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Response time line chart for selected service */}
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">
                      Response time — {SERVICES.find((s) => s.key === selectedService)?.name}
                    </h4>
                    <p className="text-xs text-gray-400">
                      Last {(histories[selectedService] || []).length} points
                    </p>
                  </div>
                </div>

                <div className="h-48 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={(histories[selectedService] || []).map((pt) => ({
                        t: new Date(pt.timestamp).toLocaleTimeString(),
                        r: pt.responseTime ?? 0,
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="t" minTickGap={20} />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="r"
                        stroke="#06b6d4"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Per-service monthly uptime small table */}
              <div className="mt-6">
                <h4 className="font-semibold">Monthly uptime (approx.)</h4>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {SERVICES.map((s) => (
                    <div
                      key={s.key}
                      className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded"
                    >
                      <div className="text-sm">{s.name}</div>
                      <div className="text-sm font-mono">{monthlyUptime[s.key] ?? '100'}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer / small legend */}
          <div className="mt-6 text-xs text-gray-400">
            <div className="mb-2">
              Legend: Operational (green) • Degraded (yellow) • Major Outage (red) • Maintenance
              (gray)
            </div>
            <div>
              Notes: If your services expose `/health/history` or `/health/uptime` endpoints, the
              dashboard will use those for accurate historical charts. Otherwise it synthesizes
              lightweight history stored in localStorage from the periodic checks.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
